// The Calendar's four views (CALENDAR-C1 §3): the week and day time grid (all-day row on top,
// the person's browser zone), the month grid (chips, "+N more" opens the day), and the agenda
// (grouped by day). Events wear their calendar's colour; a busy block is hatched and opens only
// its time; a declined event is an outline; tasks (the overlay) are quiet chips at the top of
// each day. Visible hover icons on what the person may edit: Move (drag) · Edit · Duplicate · Copy
// link · Delete, and a Resize edge at a block's foot. Drags snap to 15 minutes (a day in the month
// and the all-day row); the Edit modal stays the keyboard path for the same change.
import {
  Button,
  CALENDAR_HOUR_PX,
  CalendarBlock,
  CalendarChip,
  type CalendarChipVariant,
  CalendarDayColumn,
  CalendarNowLine,
  cn,
  EmptyState,
  Popover,
  PopoverContent,
  PopoverTrigger,
  type TagChipColor,
} from "@companyos/ui";
import {
  CalendarDaysIcon,
  CheckSquareIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  CopyIcon,
  FlagIcon,
  GripVerticalIcon,
  LinkIcon,
  PencilIcon,
  PlusIcon,
  RepeatIcon,
  Trash2Icon,
} from "lucide-react";
import {
  type DragEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import type { CalendarItem, CalendarOccurrence, CalendarTaskChip } from "../../api/types";
import { HOVER_STRIP, IconAction } from "../cadence/cadence-parts";
import {
  browserZone,
  byDay,
  clipToHours,
  clock,
  companyClock,
  type DayBuckets,
  dayLabel,
  layoutDay,
  movedTimes,
  type NewTimes,
  parseDay,
  resizedTimes,
  shownHours,
  snapMinutes,
  timeRange,
  workWindow,
  ymd,
  zoneDiffers,
  zoneShort,
} from "./calendar-model";

export type CalendarActions = {
  open: (o: CalendarOccurrence) => void;
  edit: (o: CalendarOccurrence) => void;
  duplicate: (o: CalendarOccurrence) => void;
  remove: (o: CalendarOccurrence) => void;
  copyLink: (o: CalendarOccurrence) => void;
  /** A click on an empty slot: the day and, in the time grid, the hour. */
  slot: (day: string, hour: number | null) => void;
  task: (t: CalendarTaskChip) => void;
  /** Month "+N more": that day in the day view. */
  showDay: (day: string) => void;
  /** A drag (move or resize) let go: the occurrence's new times. */
  move: (o: CalendarOccurrence, times: NewTimes) => void;
  /** CAL-WORK-LAYERS: a layer chip opened (a task's drawer, a milestone's priority). */
  item: (i: CalendarItem) => void;
  /** A work chip dropped on another day: its view's date moves there. */
  moveItem: (i: CalendarItem, day: string) => void;
};

/** What is being dragged (the native drag carries only text; the views keep the rest). */
type Dragging = { o: CalendarOccurrence; grabMinutes: number };
type DragRef = { current: Dragging | null };

const movable = (o: CalendarOccurrence) => o.can_edit && !o.busy;
const PX_PER_MINUTE = CALENDAR_HOUR_PX / 60;

function startDrag(ref: DragRef, o: CalendarOccurrence, e: DragEvent<HTMLElement>, grab = 0) {
  ref.current = { o, grabMinutes: grab };
  // Synthetic drags (tests, some touch polyfills) come without a DataTransfer.
  const dt = e.dataTransfer as DataTransfer | null;
  if (!dt) return;
  dt.effectAllowed = "move";
  dt.setData("text/plain", o.title ?? "Event");
}

/** A drop target's handlers: accept a drag `accepts` allows, hand the drop to `drop`. */
function dropTarget(
  ref: DragRef,
  accepts: (d: Dragging) => boolean,
  drop: (d: Dragging, e: DragEvent<HTMLElement>) => void,
) {
  return {
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (ref.current && accepts(ref.current)) {
        e.preventDefault();
        const dt = e.dataTransfer as DataTransfer | null;
        if (dt) dt.dropEffect = "move";
      }
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      const d = ref.current;
      ref.current = null;
      if (!d || !accepts(d)) return;
      e.preventDefault();
      drop(d, e);
    },
  };
}

/** A work chip being dragged (to another day only). */
type ItemDragRef = { current: CalendarItem | null };

/** The drop handlers of a day for a dragged work chip. */
function itemTarget(ref: ItemDragRef, day: string, move: (i: CalendarItem, day: string) => void) {
  return {
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (ref.current) {
        e.preventDefault();
        const dt = e.dataTransfer as DataTransfer | null;
        if (dt) dt.dropEffect = "move";
      }
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      const i = ref.current;
      ref.current = null;
      if (!i) return;
      e.preventDefault();
      if (itemDay(i) !== day) move(i, day);
    },
  };
}

/** Both a day's event drop and its work-chip drop. */
function bothTargets(
  a: ReturnType<typeof dropTarget>,
  b: ReturnType<typeof itemTarget>,
): ReturnType<typeof dropTarget> {
  return {
    onDragOver: (e) => {
      a.onDragOver(e);
      b.onDragOver(e);
    },
    onDrop: (e) => {
      a.onDrop(e);
      b.onDrop(e);
    },
  };
}

/** A layer chip's day: the instant's day here, else the plain calendar day. */
export const itemDay = (i: CalendarItem) => (i.at ? ymd(new Date(i.at)) : i.day);

function itemsByDay(items: readonly CalendarItem[] | undefined): Map<string, CalendarItem[]> {
  const out = new Map<string, CalendarItem[]>();
  for (const i of items ?? []) {
    const day = itemDay(i);
    out.set(day, [...(out.get(day) ?? []), i]);
  }
  return out;
}

type Shared = {
  occurrences: readonly CalendarOccurrence[];
  tasks: readonly CalendarTaskChip[] | null;
  /** CAL-WORK-LAYERS: the shown work calendars' and Milestones' chips. */
  items?: readonly CalendarItem[] | undefined;
  colorOf: (calendarId: string) => TagChipColor;
  today: string;
  actions: CalendarActions;
};

const variantOf = (o: CalendarOccurrence): CalendarChipVariant =>
  o.busy ? "busy" : o.my_response === "declined" ? "declined" : "event";

const busyLabel = (o: CalendarOccurrence) => `Busy${o.owner_name ? ` · ${o.owner_name}` : ""}`;

function tasksByDay(tasks: readonly CalendarTaskChip[] | null): Map<string, CalendarTaskChip[]> {
  const out = new Map<string, CalendarTaskChip[]>();
  for (const t of tasks ?? []) {
    const day = ymd(new Date(t.due_at));
    out.set(day, [...(out.get(day) ?? []), t]);
  }
  return out;
}

// Chips -----------------------------------------------------------------------------------------

/** A busy block's chip: a tiny popover with the time, never a detail. */
function BusyChip({ o, compact }: { o: CalendarOccurrence; compact?: boolean }) {
  return (
    <Popover>
      <PopoverTrigger
        render={
          <CalendarChip
            variant="busy"
            data-busy=""
            aria-label={`${busyLabel(o)}, ${timeRange(o)}`}
          />
        }
      >
        {!compact && !o.all_day ? <span className="tabular-nums">{clock(o.starts_at)}</span> : null}
        <span className="truncate">{busyLabel(o)}</span>
      </PopoverTrigger>
      <BusyPopover o={o} />
    </Popover>
  );
}

function BusyPopover({ o }: { o: CalendarOccurrence }) {
  return (
    <PopoverContent
      side="bottom"
      align="start"
      className="w-auto gap-1 p-3 text-xs"
      data-slot="busy-popover"
    >
      <span className="font-medium text-foreground">{busyLabel(o)}</span>
      <span className="text-muted-foreground tabular-nums">
        {dayLabel(ymd(new Date(o.starts_at)), "long")} · {timeRange(o)}
      </span>
    </PopoverContent>
  );
}

/** An event's one-line chip (all-day row, month cell). */
export function EventChip({
  o,
  color,
  actions,
  showTime = true,
  drag,
}: {
  o: CalendarOccurrence;
  color: TagChipColor;
  actions: CalendarActions;
  showTime?: boolean;
  /** Set where the chip may be dragged to another day (month, all-day row). */
  drag?: DragRef;
}) {
  if (o.busy) return <BusyChip o={o} compact={!showTime} />;
  const canDrag = Boolean(drag && movable(o));
  return (
    <CalendarChip
      color={color}
      variant={variantOf(o)}
      data-slot="calendar-event"
      data-event-id={o.event_id}
      aria-label={`${o.title ?? "Event"}, ${timeRange(o)}`}
      onClick={() => actions.open(o)}
      draggable={canDrag || undefined}
      onDragStart={canDrag && drag ? (e) => startDrag(drag, o, e) : undefined}
      onDragEnd={
        drag
          ? () => {
              drag.current = null;
            }
          : undefined
      }
      className={cn(canDrag && "cursor-grab active:cursor-grabbing")}
    >
      {showTime && !o.all_day ? (
        <span className="shrink-0 tabular-nums opacity-80">{clock(o.starts_at)}</span>
      ) : null}
      <span className="truncate">{o.title}</span>
      {o.recurring ? (
        <RepeatIcon aria-hidden="true" className="size-3 shrink-0 opacity-60" />
      ) : null}
    </CalendarChip>
  );
}

function TaskChip({ t, onOpen }: { t: CalendarTaskChip; onOpen: (t: CalendarTaskChip) => void }) {
  return (
    <CalendarChip
      variant={t.overdue ? "overdue" : "task"}
      data-slot="calendar-task"
      data-overdue={t.overdue || undefined}
      aria-label={`Task: ${t.title}${t.board_name ? ` (${t.board_name})` : ""}${t.overdue ? ", overdue" : ""}`}
      onClick={() => onOpen(t)}
      className={cn(t.done && "line-through opacity-70")}
    >
      <CheckSquareIcon aria-hidden="true" className="size-3 shrink-0" />
      <span className="truncate">{t.title}</span>
      {t.board_name ? (
        <span className="ml-auto shrink-0 truncate pl-1 opacity-70 max-sm:hidden">
          {t.board_name}
        </span>
      ) : null}
    </CalendarChip>
  );
}

/** A layer's chip: a task on a work calendar (drag to move its date) or a milestone. */
function LayerChip({
  i,
  color,
  actions,
  drag,
}: {
  i: CalendarItem;
  color: TagChipColor;
  actions: CalendarActions;
  drag?: ItemDragRef;
}) {
  const canDrag = Boolean(drag && i.kind === "task" && i.can_edit);
  const what =
    i.kind === "milestone"
      ? `Milestone${i.seat_title ? ` (${i.seat_title})` : ""}`
      : `Task${i.status_label ? ` (${i.status_label})` : ""}`;
  return (
    <CalendarChip
      color={color}
      variant="event"
      data-slot="calendar-item"
      data-kind={i.kind}
      data-done={i.done || undefined}
      aria-label={`${what}: ${i.title}${i.done ? ", done" : ""}`}
      onClick={() => actions.item(i)}
      draggable={canDrag || undefined}
      onDragStart={
        canDrag && drag
          ? (e) => {
              drag.current = i;
              const dt = e.dataTransfer as DataTransfer | null;
              if (dt) {
                dt.effectAllowed = "move";
                dt.setData("text/plain", i.title);
              }
            }
          : undefined
      }
      onDragEnd={
        drag
          ? () => {
              drag.current = null;
            }
          : undefined
      }
      className={cn(
        i.done && "line-through opacity-70",
        canDrag && "cursor-grab active:cursor-grabbing",
      )}
    >
      {i.kind === "milestone" ? <FlagIcon aria-hidden="true" className="size-3 shrink-0" /> : null}
      <span className="truncate">{i.title}</span>
      {i.status_label || i.seat_title ? (
        <span className="ml-auto max-w-[35%] shrink-0 truncate pl-1 opacity-70 max-sm:hidden">
          {i.status_label ?? i.seat_title}
        </span>
      ) : null}
    </CalendarChip>
  );
}

/** The hover icons of an event the person may edit. */
function EventControls({
  o,
  actions,
  grip = false,
}: {
  o: CalendarOccurrence;
  actions: CalendarActions;
  /** The time grid's Move handle (drag the block; the Edit modal is the keyboard path). */
  grip?: boolean;
}) {
  if (!o.can_edit || o.busy) return null;
  const name = o.title ?? "event";
  return (
    <span className={cn(HOVER_STRIP, "rounded-sm bg-card/90")} data-slot="event-controls">
      {grip ? (
        <span
          aria-hidden="true"
          title="Drag to move"
          data-slot="event-move"
          className="inline-flex size-6 cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing [&_svg]:size-3.5"
        >
          <GripVerticalIcon />
        </span>
      ) : null}
      <IconAction label={`Edit ${name}`} tip="Edit" onClick={() => actions.edit(o)}>
        <PencilIcon aria-hidden="true" />
      </IconAction>
      <IconAction label={`Duplicate ${name}`} tip="Duplicate" onClick={() => actions.duplicate(o)}>
        <CopyIcon aria-hidden="true" />
      </IconAction>
      <IconAction
        label={`Copy link to ${name}`}
        tip="Copy link"
        onClick={() => actions.copyLink(o)}
      >
        <LinkIcon aria-hidden="true" />
      </IconAction>
      <IconAction label={`Delete ${name}`} tip="Delete" danger onClick={() => actions.remove(o)}>
        <Trash2Icon aria-hidden="true" />
      </IconAction>
    </span>
  );
}

// Week and day: the time grid -------------------------------------------------------------------

const HOURS = Array.from({ length: 24 }, (_, h) => h);

export function TimeGrid({
  days,
  occurrences,
  tasks,
  items,
  colorOf,
  today,
  actions,
  companyZone,
}: Shared & {
  days: readonly string[];
  /** The company's zone: a second hour axis when it differs; its working hours fold the rest. */
  companyZone?: string | undefined;
}) {
  const buckets = byDay(occurrences, days);
  const taskDays = tasksByDay(tasks);
  const itemDays = itemsByDay(items);
  const scroller = useRef<HTMLDivElement>(null);
  const drag = useRef<Dragging | null>(null);
  const itemDrag = useRef<CalendarItem | null>(null);
  const first = days[0] ?? today;
  const zone = companyZone ?? browserZone();
  const dual = Boolean(companyZone) && zoneDiffers(zone, first);
  // Design §4: the company's working hours, the rest folded into a band that opens on click.
  const work = workWindow(first, zone);
  const [unfolded, setUnfolded] = useState(false);
  const folded = Boolean(work) && !unfolded;
  // The fold never hides an event: the shown hours widen to every timed event in view.
  const timed = days.flatMap((d) => layoutDay(d, buckets.get(d)?.timed ?? []));
  const shownRange = folded && work ? shownHours(work, timed) : { from: 0, to: 24 };
  const { from, to } = shownRange;
  // Folded: the working day from its top. Open: the working morning (07:00), not midnight; a
  // few pixels up, so the first label is not cut.
  useEffect(() => {
    if (scroller.current)
      scroller.current.scrollTop = folded
        ? 0
        : Math.max(0, (work?.from ?? 7) * CALENDAR_HOUR_PX - 12);
  }, [folded, work?.from]);
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const cols =
    days.length === 1
      ? dual
        ? "grid-cols-[96px_1fr]"
        : "grid-cols-[56px_1fr]"
      : dual
        ? "grid-cols-[96px_repeat(7,minmax(0,1fr))]"
        : "grid-cols-[56px_repeat(7,minmax(0,1fr))]";
  const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;
  // A band of empty hours (events always widen the shown range, so a band holds none).
  const band = (lo: number, hi: number, slot: string) => (
    <button
      type="button"
      data-slot={slot}
      onClick={() => setUnfolded(true)}
      aria-label={`Show ${hh(lo)}–${hh(hi)}`}
      className="flex h-6 w-full shrink-0 items-center justify-center gap-1.5 border-b border-subtle bg-sunken text-2xs text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
    >
      <ChevronsUpDownIcon aria-hidden="true" className="size-3" />
      <span className="tabular-nums">
        {hh(lo)}–{hh(hi)}
      </span>
    </button>
  );
  const minW = days.length === 1 ? "" : "min-w-[720px]";
  const anyAllDay = days.some(
    (d) =>
      (buckets.get(d)?.allDay.length ?? 0) > 0 ||
      (taskDays.get(d)?.length ?? 0) > 0 ||
      (itemDays.get(d)?.length ?? 0) > 0,
  );
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-auto" data-slot="time-grid">
      <div className={cn("flex min-h-0 flex-1 flex-col", minW)}>
        <div className={cn("grid shrink-0 border-b border-subtle", cols)}>
          {dual ? (
            <div
              className="flex items-end justify-end gap-1 pr-2 pb-1 text-2xs text-faint"
              data-slot="time-axes"
            >
              <span>{zoneShort(browserZone(), first)}</span>
              <span aria-hidden="true">·</span>
              <span title={zone}>{zoneShort(zone, first)}</span>
            </div>
          ) : (
            <div />
          )}
          {days.map((d) => (
            <div
              key={d}
              className="flex h-9 items-center justify-center gap-1 border-l border-subtle text-xs text-muted-foreground"
              aria-current={d === today ? "date" : undefined}
            >
              <span>{parseDay(d).toLocaleDateString("en-GB", { weekday: "short" })}</span>
              <span
                className={cn(
                  "inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 tabular-nums",
                  d === today
                    ? "bg-primary font-semibold text-primary-foreground"
                    : "text-foreground",
                )}
              >
                {parseDay(d).getDate()}
              </span>
            </div>
          ))}
        </div>
        <section
          className={cn("grid shrink-0 border-b border-subtle", cols, !anyAllDay && "min-h-7")}
          data-slot="all-day-row"
          aria-label="All day"
        >
          <div className="flex items-start justify-end pt-1.5 pr-2 text-2xs text-faint">
            All day
          </div>
          {days.map((d) => (
            <ul
              key={d}
              data-day={d}
              className="m-0 flex min-w-0 list-none flex-col gap-0.5 border-l border-subtle p-1"
              {...bothTargets(
                dropTarget(
                  drag,
                  (x) => x.o.all_day,
                  (x) => {
                    const t = movedTimes(x.o, d, null);
                    if (t) actions.move(x.o, t);
                  },
                ),
                itemTarget(itemDrag, d, actions.moveItem),
              )}
            >
              {(itemDays.get(d) ?? []).map((i) => (
                <li key={`i-${i.key}`}>
                  <LayerChip
                    i={i}
                    color={colorOf(i.calendar_id)}
                    actions={actions}
                    drag={itemDrag}
                  />
                </li>
              ))}
              {(taskDays.get(d) ?? []).map((t) => (
                <li key={`t-${t.id}`}>
                  <TaskChip t={t} onOpen={actions.task} />
                </li>
              ))}
              {(buckets.get(d)?.allDay ?? []).map((o) => (
                <li key={o.key}>
                  <EventChip
                    o={o}
                    color={colorOf(o.calendar_id)}
                    actions={actions}
                    showTime={false}
                    drag={drag}
                  />
                </li>
              ))}
            </ul>
          ))}
        </section>
        <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto" data-slot="time-scroll">
          {folded && from > 0 ? band(0, from, "fold-before") : null}
          {work && !folded ? (
            <button
              type="button"
              data-slot="fold"
              onClick={() => setUnfolded(false)}
              className="flex h-6 w-full items-center justify-center gap-1.5 border-b border-subtle text-2xs text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
            >
              <ChevronsDownUpIcon aria-hidden="true" className="size-3" />
              Fold to {hh(work.from)}–{hh(work.to)}
            </button>
          ) : null}
          <div className={cn("grid", cols)}>
            <div className="relative">
              {HOURS.slice(from, to).map((h) => (
                <div key={h} className="relative pr-2 text-right text-2xs text-faint tabular-nums">
                  <span className="relative -top-1.5 flex h-12 justify-end gap-1.5">
                    {h === 0 ? null : (
                      <>
                        <span>{hh(h)}</span>
                        {dual ? (
                          <span className="opacity-70" data-slot="company-hour">
                            {companyClock(first, h, zone)}
                          </span>
                        ) : null}
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
            {days.map((d) => (
              <DayColumn
                key={d}
                day={d}
                bucket={buckets.get(d)}
                colorOf={colorOf}
                actions={actions}
                drag={drag}
                nowMinutes={d === today ? nowMinutes : null}
                from={from}
                to={to}
              />
            ))}
          </div>
          {folded && to < 24 ? band(to, 24, "fold-after") : null}
        </div>
      </div>
    </div>
  );
}

function DayColumn({
  day,
  bucket,
  colorOf,
  actions,
  drag,
  nowMinutes,
  from,
  to,
}: {
  day: string;
  bucket: DayBuckets | undefined;
  colorOf: (id: string) => TagChipColor;
  actions: CalendarActions;
  drag: DragRef;
  nowMinutes: number | null;
  /** The hours shown [from, to); the rest is folded. */
  from: number;
  to: number;
}) {
  const placed = layoutDay(day, bucket?.timed ?? []);
  const label = dayLabel(day, "long");
  const offset = from * 60;
  // Resize: the bottom edge follows the pointer (captured), snapped to a quarter hour.
  const [resize, setResize] = useState<{ key: string; minute: number } | null>(null);
  const column = useRef<{ top: number } | null>(null);
  const minuteAt = (clientY: number) =>
    snapMinutes((clientY - (column.current?.top ?? 0)) / PX_PER_MINUTE + offset);
  const resizeHandlers = (o: CalendarOccurrence, top: number) => ({
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const col = e.currentTarget.closest('[data-slot="calendar-day-column"]');
      column.current = { top: col?.getBoundingClientRect().top ?? 0 };
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setResize({ key: o.key, minute: minuteAt(e.clientY) });
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      if (resize?.key !== o.key) return;
      setResize({ key: o.key, minute: Math.max(top + 15, Math.min(1440, minuteAt(e.clientY))) });
    },
    onPointerUp: (e: PointerEvent<HTMLElement>) => {
      if (resize?.key !== o.key) return;
      const t = resizedTimes(o, day, Math.max(top + 15, minuteAt(e.clientY)));
      setResize(null);
      if (t) actions.move(o, t);
    },
    onPointerCancel: () => setResize(null),
  });
  return (
    <CalendarDayColumn
      className="border-l border-subtle"
      data-day={day}
      hours={to - from}
      {...dropTarget(
        drag,
        (x) => !x.o.all_day,
        (x, e) => {
          const top = e.currentTarget.getBoundingClientRect().top;
          const minute = snapMinutes((e.clientY - top) / PX_PER_MINUTE - x.grabMinutes + offset);
          if (!Number.isFinite(minute)) return;
          const t = movedTimes(x.o, day, minute);
          if (t) actions.move(x.o, t);
        },
      )}
    >
      {HOURS.slice(from, to).map((h) => (
        <button
          key={h}
          type="button"
          aria-label={`New event ${label} at ${String(h).padStart(2, "0")}:00`}
          onClick={() => actions.slot(day, h)}
          className="block h-12 w-full border-b border-subtle/70 hover:bg-muted/40 focus-visible:bg-muted/60 focus-visible:outline-none"
        />
      ))}
      {placed
        .flatMap(({ o, top, height, lane, lanes }) => {
          const shown = clipToHours(
            { top, height: resize?.key === o.key ? resize.minute - top : height },
            from,
            to,
          );
          return shown ? [{ o, top, shown, lane, lanes }] : [];
        })
        .map(({ o, top, shown, lane, lanes }) => (
          <CalendarBlock
            key={o.key}
            top={shown.top}
            height={shown.height}
            lane={lane}
            lanes={lanes}
            color={colorOf(o.calendar_id)}
            variant={variantOf(o)}
            data-slot="calendar-event-block"
            data-resizing={resize?.key === o.key || undefined}
            draggable={movable(o) || undefined}
            onDragStart={
              movable(o)
                ? (e) => {
                    if (resize) {
                      e.preventDefault();
                      return;
                    }
                    const r = e.currentTarget.getBoundingClientRect();
                    startDrag(drag, o, e, (e.clientY - r.top) / PX_PER_MINUTE || 0);
                  }
                : undefined
            }
            onDragEnd={() => {
              drag.current = null;
            }}
            className={cn(movable(o) && "cursor-grab active:cursor-grabbing")}
          >
            {o.busy ? (
              <Popover>
                <PopoverTrigger
                  className="flex size-full flex-col items-start px-1.5 py-0.5 text-left"
                  data-busy=""
                  aria-label={`${busyLabel(o)}, ${timeRange(o)}`}
                >
                  <span className="truncate font-medium">{busyLabel(o)}</span>
                </PopoverTrigger>
                <BusyPopover o={o} />
              </Popover>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => actions.open(o)}
                  aria-label={`${o.title ?? "Event"}, ${timeRange(o)}`}
                  data-slot="calendar-event"
                  className="flex size-full flex-col items-start overflow-hidden px-1.5 py-0.5 text-left focus-visible:outline-2 focus-visible:outline-ring"
                >
                  <span className="flex w-full min-w-0 items-center gap-1 font-medium">
                    <span className="truncate">{o.title}</span>
                    {o.recurring ? (
                      <RepeatIcon aria-hidden="true" className="size-3 shrink-0 opacity-60" />
                    ) : null}
                  </span>
                  {shown.height >= 40 ? (
                    <span className="truncate tabular-nums opacity-80">
                      {timeRange(o)}
                      {o.location ? ` · ${o.location}` : ""}
                    </span>
                  ) : null}
                </button>
                <span className="absolute top-0.5 right-0.5">
                  <EventControls o={o} actions={actions} grip />
                </span>
                {movable(o) ? (
                  <span
                    aria-hidden="true"
                    title="Drag to resize"
                    data-slot="event-resize"
                    className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize touch-none bg-current opacity-0 transition-opacity group-hover/row:opacity-30 pointer-coarse:opacity-30"
                    {...resizeHandlers(o, top)}
                  />
                ) : null}
              </>
            )}
          </CalendarBlock>
        ))}
      {nowMinutes !== null && nowMinutes >= offset && nowMinutes < to * 60 ? (
        <CalendarNowLine minutes={nowMinutes - offset} />
      ) : null}
    </CalendarDayColumn>
  );
}

// Month -----------------------------------------------------------------------------------------

const MAX_PER_DAY = 3;

export function MonthGrid({
  days,
  anchor,
  occurrences,
  tasks,
  items,
  colorOf,
  today,
  actions,
}: Shared & { days: readonly string[]; anchor: string }) {
  const buckets = byDay(occurrences, days);
  const taskDays = tasksByDay(tasks);
  const itemDays = itemsByDay(items);
  const month = parseDay(anchor).getMonth();
  const drag = useRef<Dragging | null>(null);
  const itemDrag = useRef<CalendarItem | null>(null);
  const weeks: string[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return (
    <div className="min-h-0 flex-1 overflow-auto p-4 max-md:p-2" data-slot="month-grid">
      <table className="w-full min-w-[640px] table-fixed border-collapse" aria-label="Month">
        <thead>
          <tr>
            {weeks[0]?.map((d) => (
              <th
                key={d}
                scope="col"
                className="h-8 px-2 text-left text-xs font-medium text-muted-foreground"
              >
                {parseDay(d).toLocaleDateString("en-GB", { weekday: "short" })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]}>
              {week.map((d) => {
                const b = buckets.get(d);
                const list = [...(b?.allDay ?? []), ...(b?.timed ?? [])];
                const ts = taskDays.get(d) ?? [];
                // Three chips a day: the tasks overlay, then events, then the layers' chips in
                // what room is left (a layer never pushes an event into "+N more").
                const tsShown = ts.slice(0, MAX_PER_DAY);
                const room = Math.max(0, MAX_PER_DAY - ts.length);
                const shown = list.slice(0, room);
                const allItems = itemDays.get(d) ?? [];
                const is = allItems.slice(0, Math.max(0, room - shown.length));
                const more = list.length - shown.length + (allItems.length - is.length);
                const outside = parseDay(d).getMonth() !== month;
                return (
                  <td
                    key={d}
                    data-day={d}
                    aria-current={d === today ? "date" : undefined}
                    {...bothTargets(
                      dropTarget(
                        drag,
                        () => true,
                        (x) => {
                          // Another day, the same time of day.
                          const t = movedTimes(x.o, d, null);
                          if (t) actions.move(x.o, t);
                        },
                      ),
                      itemTarget(itemDrag, d, actions.moveItem),
                    )}
                    className={cn(
                      "group/day h-28 border border-subtle p-1 align-top",
                      outside ? "bg-sunken" : "bg-card",
                    )}
                  >
                    <div className="mb-0.5 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => actions.showDay(d)}
                        aria-label={`Open ${dayLabel(d, "long")}`}
                        className={cn(
                          "inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs tabular-nums hover:bg-muted",
                          d === today
                            ? "bg-primary font-semibold text-primary-foreground hover:bg-primary"
                            : outside
                              ? "text-muted-foreground"
                              : "text-secondary",
                        )}
                      >
                        {parseDay(d).getDate()}
                      </button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`New event ${dayLabel(d, "long")}`}
                        onClick={() => actions.slot(d, null)}
                        className="text-faint opacity-0 group-hover/day:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                      >
                        <PlusIcon aria-hidden="true" />
                      </Button>
                    </div>
                    <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                      {is.map((i) => (
                        <li key={`i-${i.key}`}>
                          <LayerChip
                            i={i}
                            color={colorOf(i.calendar_id)}
                            actions={actions}
                            drag={itemDrag}
                          />
                        </li>
                      ))}
                      {tsShown.map((t) => (
                        <li key={`t-${t.id}`}>
                          <TaskChip t={t} onOpen={actions.task} />
                        </li>
                      ))}
                      {shown.map((o) => (
                        <li key={o.key}>
                          <EventChip
                            o={o}
                            color={colorOf(o.calendar_id)}
                            actions={actions}
                            drag={drag}
                          />
                        </li>
                      ))}
                    </ul>
                    {more > 0 ? (
                      <button
                        type="button"
                        onClick={() => actions.showDay(d)}
                        className="mt-0.5 h-5 rounded-sm px-1.5 text-2xs text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        +{more} more
                      </button>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Agenda ----------------------------------------------------------------------------------------

export function AgendaList({
  days,
  occurrences,
  tasks,
  items,
  colorOf,
  today,
  actions,
}: Shared & { days: readonly string[] }) {
  const buckets = byDay(occurrences, days);
  const taskDays = tasksByDay(tasks);
  const itemDays = itemsByDay(items);
  const shown = days.filter(
    (d) =>
      (buckets.get(d)?.allDay.length ?? 0) +
        (buckets.get(d)?.timed.length ?? 0) +
        (taskDays.get(d)?.length ?? 0) +
        (itemDays.get(d)?.length ?? 0) >
      0,
  );
  if (!shown.length)
    return (
      <EmptyState
        variant="compact"
        icon={CalendarDaysIcon}
        className="mt-10"
        title="Nothing in these two weeks"
      />
    );
  return (
    <div className="min-h-0 flex-1 overflow-auto px-6 py-4 max-md:px-4" data-slot="agenda">
      {shown.map((d) => {
        const b = buckets.get(d);
        const rows: ReactNode[] = [
          ...(itemDays.get(d) ?? []).map((i) => (
            <li key={`i-${i.key}`} className="flex items-center gap-3 py-1">
              <span className="w-24 shrink-0 text-xs text-faint">
                {i.kind === "milestone" ? "Milestone" : "Work"}
              </span>
              <span className="min-w-0 flex-1">
                <LayerChip i={i} color={colorOf(i.calendar_id)} actions={actions} />
              </span>
            </li>
          )),
          ...(taskDays.get(d) ?? []).map((t) => (
            <li key={`t-${t.id}`} className="flex items-center gap-3 py-1">
              <span className="w-24 shrink-0 text-xs text-faint">Due</span>
              <span className="min-w-0 flex-1">
                <TaskChip t={t} onOpen={actions.task} />
              </span>
            </li>
          )),
          ...[...(b?.allDay ?? []), ...(b?.timed ?? [])].map((o) => (
            <li
              key={o.key}
              className="group/row flex items-center gap-3 py-1"
              data-slot="agenda-row"
            >
              <span className="w-24 shrink-0 text-xs text-muted-foreground tabular-nums">
                {timeRange(o)}
              </span>
              <span className="min-w-0 flex-1">
                <EventChip
                  o={o}
                  color={colorOf(o.calendar_id)}
                  actions={actions}
                  showTime={false}
                />
              </span>
              <EventControls o={o} actions={actions} />
            </li>
          )),
        ];
        return (
          <section key={d} className="mb-4" aria-label={dayLabel(d, "long")}>
            <h2
              className={cn(
                "mb-1 text-xs font-semibold",
                d === today ? "text-primary-text" : "text-foreground",
              )}
            >
              {dayLabel(d, "long")}
              {d === today ? (
                <span className="ml-1.5 font-normal text-muted-foreground">Today</span>
              ) : null}
            </h2>
            <ul className="m-0 list-none divide-y divide-subtle p-0">{rows}</ul>
          </section>
        );
      })}
    </div>
  );
}
